import {get} from 'object-path-immutable'
import {getCardInputs} from '../../../../../common/action-flows/helpers'
import {inferProperties} from '../../../../../common/integrations/payload-mapper'
import {
  AnalyticsEventType,
  CardDefinition,
  UIElementType
} from '../../../../../common/types'
import {
  ActionFlowConfig,
  ActionFlowStep,
  AugmentedContextStepData,
  AugmentedEventListenerOutput,
  AugmentedFlowContext,
  CardEventListenerStep,
  GoalStep,
  GoalStepOutput,
  SendRequestStep,
  WaitStep,
  WaitStepOutput
} from '../../../../../common/types/action-flows'

type ContextOption = {
  id: string
  type: 'option' | 'eventOption'
  name: string // displays in pill
  path: string // path into augmented context
  label?: string // label to display in dropdown
  valueType?: string // 'interval' or other string is for primitive type of value (derived using 'typeof' operator)
}

type CardCompletedContextOption = Omit<ContextOption, 'valueType' | 'type'> & {
  type: 'input'
  inputId: string
  inputType?: UIElementType
}

type CardContextOption = CardCompletedContextOption | ContextOption

type CardStepContext = {
  type: 'step'
  step: CardEventListenerStep
  optionType: 'card'
  options: CardContextOption[]
}

type SendRequestContext = {
  type: 'step'
  step: SendRequestStep
  optionType: 'request'
  options: ContextOption[]
}

type GoalStepContext = {
  type: 'step'
  step: GoalStep
  optionType: 'goal'
  options: ContextOption[]
}

type WaitStepContext = {
  type: 'step'
  step: WaitStep
  optionType: 'wait'
  options: ContextOption[]
}

type EventContext = {
  type: 'step'
  step: ActionFlowStep
  optionType: 'event'
  options: ContextOption[]
}

export type ProfileContext = {
  type: 'profile'
  options: ContextOption[]
}

export type StepContext =
  | CardStepContext
  | SendRequestContext
  | EventContext
  | GoalStepContext
  | WaitStepContext

export type ContextGroup = StepContext | ProfileContext

export type OptionType = CardContextOption['type']

const getEventOptions = (
  stepId: string,
  step: AugmentedContextStepData
): ContextOption[] => {
  if (step.output && 'event' in step.output) {
    return inferProperties(step.output.event, true).map(path => ({
      id: path.replace(/\./g, '_'),
      name: path.replace(/\./g, '_'),
      type: 'eventOption',
      label: path,
      path: `context.flowSteps['${stepId}'].output.event.${path}`,
      valueType: typeof get(step, `output.event.${path}`)
    }))
  }
  return []
}

const getGoalStepOptions = (
  step: GoalStep,
  output: GoalStepOutput
): GoalStepContext | undefined => {
  if (output.goalValueMeasure === 'none') return undefined
  return {
    step,
    type: 'step',
    optionType: 'goal',
    options: Object.entries(output).map(([propName, propValue]) => ({
      id: propName,
      name: propName,
      path: `context.flowSteps['${step.id}'].output.${propName}`,
      type: 'option',
      label: propName,
      valueType: typeof propValue
    }))
  }
}

const getWaitStepOptions = (
  step: WaitStep,
  output: WaitStepOutput
): WaitStepContext => {
  return {
    step,
    type: 'step',
    optionType: 'wait',
    options: Object.entries(output).map(([propName, propValue]) => ({
      id: propName,
      name: propName,
      path: `context.flowSteps['${step.id}'].output.${propName}`,
      type: 'option',
      label: propName,
      valueType: typeof propValue
    }))
  }
}

export const getContextGroups = (
  flowConfig: ActionFlowConfig,
  mappedSample: AugmentedFlowContext
): ContextGroup[] => {
  const steps = Object.entries(mappedSample.context.flowSteps ?? {}).reduce<
    ContextGroup[]
  >((groups, [id, step]) => {
    if (step.type === 'event-listener' && 'card' in step.output) {
      const stepContext: CardStepContext = {
        step: flowConfig.steps[id] as CardEventListenerStep,
        type: 'step',
        optionType: 'card',
        options: [
          ...getCardOutputs(
            step.output,
            flowConfig,
            `context.flowSteps['${id}'].output`
          ),
          ...getEventOptions(id, step)
        ]
      }

      return [...groups, stepContext]
    }

    if (step.type === 'send-request') {
      const stepContext: SendRequestContext = {
        step: flowConfig.steps[id] as SendRequestStep,
        type: 'step',
        optionType: 'request',
        options: inferProperties(step.output.body, true).map(path => ({
          id: path.replace(/\./g, '_'),
          name: path.replace(/\./g, '_'),
          type: 'option',
          label: path,
          path: `context.flowSteps['${id}'].output.body.${path}`,
          valueType: typeof get(step.output.body, path)
        }))
      }

      return [...groups, stepContext]
    }

    if (step.type === 'goal') {
      const stepContext = getGoalStepOptions(
        flowConfig.steps[id] as GoalStep,
        step.output
      )
      if (!stepContext) return groups
      return [...groups, stepContext]
    }

    if (step.type === 'wait') {
      const stepContext = getWaitStepOptions(
        flowConfig.steps[id] as WaitStep,
        step.output
      )
      return [...groups, stepContext]
    }

    if (step.output && 'event' in step.output) {
      const stepContext: EventContext = {
        type: 'step',
        optionType: 'event',
        step: flowConfig.steps[id],
        options: getEventOptions(id, step)
      }
      return [...groups, stepContext]
    }

    return groups
  }, [])

  const profileContext: ProfileContext = {
    type: 'profile',
    options: Object.keys(mappedSample.context.profileValues ?? {}).map(
      field => {
        const safe = field.trim().toLowerCase().replace(/[^\w]/g, '_')
        return {
          id: safe,
          type: 'option',
          name: safe,
          label: field,
          path: `context.profileValues.${field}`,
          valueType: typeof mappedSample.context.profileValues?.[field]
        }
      }
    )
  }

  return [...steps, profileContext]
}

const getCardOutputs = (
  output: AugmentedEventListenerOutput,
  config: ActionFlowConfig,
  path: string
): CardContextOption[] => {
  const cardOutput = output.card
  if (!cardOutput.cardTemplateId) return []
  const cardDefinition = config?.cardTemplates?.[cardOutput.cardTemplateId]
  if (!cardDefinition) return []
  const inputs = getCardInputs(cardDefinition as CardDefinition)
  const cardCompletedOutputs = Object.keys(cardOutput.output).reduce<
    CardCompletedContextOption[]
  >((acc, key) => {
    const input = inputs.find(i => i.key === key)
    if (input) {
      const label =
        //@ts-ignore
        input.attributes?.label ||
        //@ts-ignore
        input.attributes?.placeholder ||
        //@ts-ignore
        input.attributes.text
      return [
        ...acc,
        {
          id: key,
          type: 'input',
          inputId: key,
          label: label,
          //@ts-ignore
          name: (input.attributes?.name ?? key) as string,
          inputType: input.type,
          path: `${path}.card.output['${key}']`
        }
      ]
    }
    return [
      ...acc,
      {
        id: key,
        type: 'input',
        inputId: key,
        label: key,
        name: key,
        inputType: 'submitButton',
        path: `${path}.card.output['${key}']`
      }
    ]
  }, [])

  const outputs: CardContextOption[] = cardCompletedOutputs

  switch (output.event?.eventName as AnalyticsEventType) {
    case 'card-snoozed': {
      // eslint-disable-next-line functional/immutable-data
      outputs.push({
        id: 'snooze_period',
        type: 'option',
        name: 'snooze_period',
        path: `${path}.event.properties.snoozePeriod`,
        label: 'Snooze Period',
        valueType: 'interval'
      })
      break
    }
    case 'card-voted-down': {
      // eslint-disable-next-line functional/immutable-data
      outputs.push({
        id: 'vote_down_reason',
        type: 'option',
        name: 'vote_down_reason',
        path: `${path}.event.properties.reason`,
        label: 'Voted down reason',
        valueType: 'string'
      })
      // eslint-disable-next-line functional/immutable-data
      outputs.push({
        id: 'vote_down_other_message',
        type: 'option',
        name: 'vote_down_other_message',
        path: `${path}.event.properties.message`,
        label: 'Voted down message',
        valueType: 'string'
      })
      break
    }
  }

  return outputs
}
